// src/Dashboard/pages/teacher/SuiviCompetitions/SuiviCompetitionsDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import { useSuiviCompetitionsDashboard } from "./hooks/useSuiviCompetitionsDashboard";

import SuiviCompetitionsHeader from "./components/SuiviCompetitionsHeader";
import SuiviCompetitionsFiltersCard from "./components/SuiviCompetitionsFiltersCard";
import SuiviCompetitionsKpiCards from "./components/SuiviCompetitionsKpiCards";
import SuiviCompetitionsEmptyState from "./components/SuiviCompetitionsEmptyState";
import CompetitionEventCard from "./components/CompetitionEventCard";
import CompetitionRegisterDrawer from "./components/CompetitionRegisterDrawer";
import CompetitionUnregisterDrawer from "./components/CompetitionUnregisterDrawer";
import CompetitionResultDrawer from "./components/CompetitionResultDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getRoleId(user, storedUser) {
  return Number(
    user?.role_id ||
      user?.roleId ||
      storedUser?.role_id ||
      storedUser?.roleId ||
      0
  );
}

function UnauthorizedPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Suivi des compétitions
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs et enseignants.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function SuiviCompetitionsDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user, storedUser);

  const authorized = roleId === 1 || roleId === 3;
  const isAdmin = roleId === 1;
  const isTeacher = roleId === 3;

  const {
    events,
    filteredEvents,
    visibleEvents,
    eventTypes,
    stats,

    filters,
    setFilters,
    resetFilters,

    enseignants,
    countsMap,

    expandedIds,
    registrantsByEvent,
    registrantsLoadingMap,

    loading,
    hasMore,
    loadMore,
    refresh,

    toggleEvent,

    registerDrawer,
    openRegisterDrawer,
    closeRegisterDrawer,

    unregisterDrawer,
    openUnregisterDrawer,
    closeUnregisterDrawer,

    resultDrawer,
    openResultDrawer,
    closeResultDrawer,

    refreshEventRegistrants,
  } = useSuiviCompetitionsDashboard();

  if (!authorized) {
    return <UnauthorizedPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <SuiviCompetitionsHeader
          loading={loading}
          onRefresh={refresh}
          isAdmin={isAdmin}
          isTeacher={isTeacher}
        />

        <SuiviCompetitionsFiltersCard
          filters={filters}
          setFilters={setFilters}
          eventTypes={eventTypes}
          loading={loading}
          onReset={resetFilters}
        />

        <SuiviCompetitionsKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Compétitions & événements
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Inscriptions, résultats et palmarès
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Dépliez une compétition pour voir les inscrits, inscrire ou
                  désinscrire un adhérent, puis saisir les résultats et alimenter
                  automatiquement le palmarès.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Résultats :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredEvents.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {events.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-32 animate-pulse rounded-[24px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : visibleEvents.length === 0 ? (
              <SuiviCompetitionsEmptyState onReset={resetFilters} />
            ) : (
              <div className="mt-5 space-y-4">
                {visibleEvents.map((event) => {
                  const registrants = registrantsByEvent[event.id] || [];
                  const expanded = expandedIds.has(event.id);

                  return (
                    <CompetitionEventCard
                      key={event.id}
                      event={event}
                      enseignants={enseignants}
                      count={countsMap[event.id]}
                      expanded={expanded}
                      registrants={registrants}
                      loadingRegistrants={!!registrantsLoadingMap[event.id]}
                      onToggle={() => toggleEvent(event)}
                      onRegister={() => openRegisterDrawer(event)}
                      onUnregister={() =>
                        openUnregisterDrawer(event, registrants)
                      }
                      onOpenResult={(adherent) =>
                        openResultDrawer(event, adherent)
                      }
                    />
                  );
                })}

                {hasMore && (
                  <div className="flex justify-center pt-2">
                    <button
                      type="button"
                      onClick={loadMore}
                      className="inline-flex items-center rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white shadow transition hover:bg-indigo-800"
                    >
                      Afficher plus
                      <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                        {visibleEvents.length}/{filteredEvents.length}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      <CompetitionRegisterDrawer
        isOpen={registerDrawer.open}
        event={registerDrawer.event}
        registeredIds={registerDrawer.registeredIds}
        onClose={closeRegisterDrawer}
        onRegistered={() => refreshEventRegistrants(registerDrawer.event?.id)}
      />

      <CompetitionUnregisterDrawer
        isOpen={unregisterDrawer.open}
        event={unregisterDrawer.event}
        adherents={unregisterDrawer.adherents}
        onClose={closeUnregisterDrawer}
        onUnregistered={() =>
          refreshEventRegistrants(unregisterDrawer.event?.id)
        }
      />

      <CompetitionResultDrawer
        isOpen={resultDrawer.open}
        event={resultDrawer.event}
        adherent={resultDrawer.adherent}
        results={resultDrawer.results}
        loading={resultDrawer.loading}
        enseignants={enseignants}
        onClose={closeResultDrawer}
        onSaved={() => refreshEventRegistrants(resultDrawer.event?.id)}
      />

      <ToastContainer />
    </section>
  );
}